import {createUserManager} from '@sdb-develop/learning-redux-oidc-es6';
import OidcClient from '@sdb-develop/learning-oidc-client/index.js';
import {leerplatformSettingsStore} from '@/shared/stores/leerplatformSettingsStore';
import type {EnvironmentConfig} from '@/shared/stores/leerplatformSettingsStore';

// Define TypeScript interfaces
interface UserManagerConfig {
    client_id: string;
    redirect_uri: string;
    post_logout_redirect_uri: string;
    response_type: string;
    scope: string;
    authority: string;
    silent_redirect_uri: string;
    automaticSilentRenew: boolean;
    filterProtocolClaims: boolean;
    loadUserInfo: boolean;
    userStore: OidcClient.WebStorageStateStore;
}

let environmentConfig: EnvironmentConfig | null = null;

async function fetchEnvironmentConfig(): Promise<EnvironmentConfig> {
    if (environmentConfig) {
        return environmentConfig;
    }

    try {
        const config = await leerplatformSettingsStore.loadEnvironmentConfig();
        environmentConfig = config;
        return config;
    } catch (error) {
        // De terugval hieronder levert lege URL's op, waarna de applicatie
        // doorstart in een onbruikbare staat - inclusief een lege `authority`,
        // dus ook inloggen faalt. Hij blijft voorlopig staan omdat weghalen het
        // opstarten anders helemaal breekt, maar hij moet wél meetbaar zijn:
        // tot nu toe was dit alleen te zien aan cryptische fouten verderop, en
        // dat maakte het op 10 september onvindbaar.
        console.error('Error fetching environment config:', error);

        const insights = (
            window as typeof window & {
                appInsights?: {trackEvent: (event: {name: string; properties?: unknown}) => void};
            }
        ).appInsights;

        insights?.trackEvent({
            name: 'environment config fallback used',
            properties: {url: window.location.href, error: String(error)},
        });

        // Return a default config as fallback
        return {
            hlUrl: '',
            skillsApiUrl: '',
            catalogApiUrl: '',
            accreditationApiUrl: '',
            messagesApiUrl: '',
            ssoApiUrl: '',
            domSecManagementApiUrl: '',
            accessManagementApiUrl: '',
            academyDepartmentsUrl: '',
            planningIntegrationApiUrl: '',
            reportApiUrl: '',
            importApiUrl: '',
            notificationsApiUrl: '',
            apiUrl: '',
            authority: 'https://sts-dev.competence.biz/identity',
        };
    }
}

export function getClientId(): string {
    switch (window.location.hostname) {
        case 'localhost':
        case 'develop.test-sdbleerplatform.nl':
            return 'https://develop5.tcg-academy.nl';
        default:
            return `https://${window.location.hostname}`;
    }
}

export function getAuthoritySync(): string {
    if (environmentConfig && environmentConfig.authority) {
        return environmentConfig.authority;
    }

    // Fallback to original logic until environment is loaded
    return [
        'localhost',
        'develop.tcg-minerva.nl',
        'test.tcg-minerva.nl',
        'develop.test-sdbleerplatform.nl',
        'test.test-sdbleerplatform.nl',
    ].indexOf(window.location.hostname.toLowerCase()) === -1
        ? 'https://sts.competence.biz/identity'
        : 'https://sts-dev.competence.biz/identity';
}

// Start fetching the environment config immediately
const environmentPromise = fetchEnvironmentConfig();

function createUserManagerConfig(authority: string): UserManagerConfig {
    return {
        client_id: getClientId(),
        redirect_uri: `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/callback`,
        post_logout_redirect_uri: `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/`,
        response_type: 'token id_token',
        scope: 'openid name api api_tcg_claims idString email',
        authority: authority,
        silent_redirect_uri: `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/silent_renew`,
        automaticSilentRenew: true,
        filterProtocolClaims: false,
        loadUserInfo: false,
        userStore: new OidcClient.WebStorageStateStore({
            store: window.sessionStorage,
        }),
    };
}

// Create a user manager with the authority from environment.json or fallback
const userManager = createUserManager(createUserManagerConfig(getAuthoritySync()));

// De cijfers achter #6609 kwamen uit `pageViews` op /silent_renew, en die rijen
// bestonden alleen doordat de applicatie in dat iframe opstartte. Nu daar een
// statische pagina staat verdwijnen ze - of het probleem nu opgelost is of niet.
// Zonder dit gebeurtenisje meten we straks onze eigen wijziging in plaats van
// het resultaat, en zijn we blind op precies de plek waar dit gevonden is.
//
// Tegelijk is dit de eerste keer dat zo'n fout überhaupt zichtbaar is: voorheen
// bleef hij in het iframe hangen, waar silent-renew-callback hem in een lege
// catch liet vallen.
export function trackAuthEvent(name: string, error?: unknown): void {
    const message =
        error === undefined ? '' : String((error as {message?: string})?.message ?? error);

    // Ook naar de console: zonder dit is een mislukte vernieuwing lokaal
    // onzichtbaar, want App Insights draait daar niet. De oude code in het
    // iframe deed tenminste nog een console.error.
    console.error(name, message);

    const insights = (
        window as typeof window & {
            appInsights?: {trackEvent: (event: {name: string; properties?: unknown}) => void};
        }
    ).appInsights;

    insights?.trackEvent({name, properties: {error: message}});
}

userManager.events?.addSilentRenewError?.((error: unknown) =>
    trackAuthEvent('silent renew failed', error),
);

// Twee mechanismen laden /silent_renew, niet één. Naast het vernieuwen van het
// token roept SessionMonitor `querySessionStatus()` aan - bij het opstarten en
// bij elke wijziging van de sessiestatus - en dat gebruikt dezelfde
// silent_redirect_uri. `monitorSession` staat standaard aan en wij zetten hem
// nergens uit.
//
// Mislukt zo'n controle, dan roept oidc-client `userSignedOut` en houdt het
// daarmee op. Wat we daarmee doen staat in auth/stsSignOut.ts.

// Export the async function for components that need to wait for the real authority
export async function getAuthority(): Promise<string> {
    const config = await environmentPromise;
    return config.authority;
}

export default userManager;
